import core from '@bravissimolabs/website-core';
import config from 'config';
import { logoutSuccess, loginSuccess } from '../../actions/auth';
import {
  AUTHENTICATED_TOKEN_STORAGE_KEY,
  IDENTIFIED_TOKEN_STORAGE_KEY,
} from '../../constants/auth';

/**
 * Factory that wraps a populateStore method with authentication checks.
 * - If the current state fails the authentication criteria checks then throw a 401.
 * - If the current state contains a user that no longer exists then throw a 403, and remove state and cookies.
 * - If authentication passes then call the original populate store function.
 *
 * @param {Promise|Function} originalPopulateStore
 * @param {String} [authenticationCriteria] either isAuthenticated or isIdentified
 * @returns {Promise}
 */
export default function addAuthenticationToPopulateStore(
  originalPopulateStore,
  authenticationCriteria
) {
  return async function populateStoreAuthenticated(
    store,
    renderProps,
    req,
    res
  ) {
    const { auth } = store.getState();

    // Verify authentication
    const isAuthenticated = auth && auth[authenticationCriteria];

    if (!isAuthenticated) {
      // Current state suggests we are not authenticated. Do not run populate store function.
      res.status(401);
      return null;
    }

    // Check the DB
    const user = await core.customer.customer.getCustomerById(auth.user.id);

    if (!user) {
      // User does not exist in the DB.
      // Must have some old cookies hanging around.
      // Bin off state that suggests they are logged in and clear the cookies.
      const domain = config.cookieDomain ? config.cookieDomain : req.hostname;
      res.clearCookie(IDENTIFIED_TOKEN_STORAGE_KEY, { domain });
      res.clearCookie(AUTHENTICATED_TOKEN_STORAGE_KEY, { domain });
      store.dispatch(logoutSuccess());

      // The user will be redirected to the login screen now. Ensure a forbidden response is thrown.
      res.status(403);
      return null;
    }

    // Update the state with the correct user
    store.dispatch(loginSuccess({ user }));

    // All OK run the original populate store function.
    return originalPopulateStore(store, renderProps, req, res);
  };
}
